import React, { useEffect, useMemo, useState } from 'react';
import { Check, ChevronLeft, LoaderCircle, Plus, Trash2 } from 'lucide-react';
import { formatPhone, onlyNumbers } from './formularioParts/utils';
import { SignaturePad } from './formularioParts/components/SignaturePad';
import { CriancaEvento, fetchCriancasEvento } from './eventoAdicionalData';
import { PessoaRetiradaTermo, enviarTermoAutorizacao } from './termoAutorizacaoData';

const normalizeText = (value = '') =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const formatCpf = (value: string) =>
  onlyNumbers(value)
    .slice(0, 11)
    .replace(/^(\d{3})(\d)/, '$1.$2')
    .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');

type Etapa = 'crianca' | 'dados' | 'assinatura';

const TermoAutorizacaoPublico: React.FC = () => {
  const [etapa, setEtapa] = useState<Etapa>('crianca');

  const [criancas, setCriancas] = useState<CriancaEvento[]>([]);
  const [buscaCrianca, setBuscaCrianca] = useState('');
  const [criancaSelecionada, setCriancaSelecionada] = useState<CriancaEvento | null>(null);
  const [carregandoDados, setCarregandoDados] = useState(true);
  const [erroCarregamento, setErroCarregamento] = useState('');

  const [nomeResponsavel, setNomeResponsavel] = useState('');
  const [cpfResponsavel, setCpfResponsavel] = useState('');
  const [telefoneResponsavel, setTelefoneResponsavel] = useState('');
  const [telefoneEmergencia, setTelefoneEmergencia] = useState('');
  const [alergias, setAlergias] = useState('');
  const [medicamentos, setMedicamentos] = useState('');
  const [restricoesAlimentares, setRestricoesAlimentares] = useState('');
  const [outrasInformacoes, setOutrasInformacoes] = useState('');
  const [convenioMedico, setConvenioMedico] = useState('');
  const [numeroCarteirinha, setNumeroCarteirinha] = useState('');
  const [autorizaImagem, setAutorizaImagem] = useState<'sim' | 'nao' | ''>('');
  const [pessoasRetirada, setPessoasRetirada] = useState<PessoaRetiradaTermo[]>([{ nome: '', cpf: '' }]);

  const [assinaturaDesenhada, setAssinaturaDesenhada] = useState(false);
  const [assinaturaUrl, setAssinaturaUrl] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [erroEnvio, setErroEnvio] = useState('');
  const [enviado, setEnviado] = useState(false);

  useEffect(() => {
    let ativo = true;
    fetchCriancasEvento()
      .then((lista) => {
        if (ativo) setCriancas(lista);
      })
      .catch((error: Error) => {
        if (ativo) setErroCarregamento(error.message);
      })
      .finally(() => {
        if (ativo) setCarregandoDados(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  const sugestoes = useMemo(() => {
    const busca = normalizeText(buscaCrianca.trim());
    if (busca.length < 2) return [];
    return criancas.filter((crianca) => normalizeText(crianca.nomeCompleto).includes(busca)).slice(0, 8);
  }, [buscaCrianca, criancas]);

  const selecionarCrianca = (crianca: CriancaEvento) => {
    setCriancaSelecionada(crianca);
    setBuscaCrianca('');
  };

  const dadosValidos =
    nomeResponsavel.trim().length > 2 &&
    onlyNumbers(cpfResponsavel).length === 11 &&
    onlyNumbers(telefoneResponsavel).length >= 10 &&
    autorizaImagem !== '' &&
    pessoasRetirada.some((pessoa) => pessoa.nome.trim());

  const alterarPessoaRetirada = (indice: number, patch: Partial<PessoaRetiradaTermo>) => {
    setPessoasRetirada((atual) => atual.map((pessoa, index) => (index === indice ? { ...pessoa, ...patch } : pessoa)));
  };

  const adicionarPessoaRetirada = () => {
    setPessoasRetirada((atual) => (atual.length >= 3 ? atual : [...atual, { nome: '', cpf: '' }]));
  };

  const removerPessoaRetirada = (indice: number) => {
    setPessoasRetirada((atual) => (atual.length <= 1 ? atual : atual.filter((_, index) => index !== indice)));
  };

  const enviarTermo = async () => {
    if (!criancaSelecionada || !assinaturaUrl) return;
    setEnviando(true);
    setErroEnvio('');
    try {
      await enviarTermoAutorizacao({
        inscricaoId: criancaSelecionada.id,
        nomeResponsavel: nomeResponsavel.trim(),
        cpfResponsavel: onlyNumbers(cpfResponsavel),
        telefoneResponsavel: onlyNumbers(telefoneResponsavel),
        telefoneEmergencia: onlyNumbers(telefoneEmergencia),
        alergias: alergias.trim(),
        medicamentos: medicamentos.trim(),
        restricoesAlimentares: restricoesAlimentares.trim(),
        outrasInformacoes: outrasInformacoes.trim(),
        convenioMedico: convenioMedico.trim(),
        numeroCarteirinha: numeroCarteirinha.trim(),
        autorizaImagem: autorizaImagem === 'sim',
        pessoasRetirada: pessoasRetirada
          .map((pessoa) => ({ nome: pessoa.nome.trim(), cpf: onlyNumbers(pessoa.cpf) }))
          .filter((pessoa) => pessoa.nome),
        assinaturaUrl
      });
      setEnviado(true);
    } catch (error) {
      setErroEnvio((error as Error).message);
    } finally {
      setEnviando(false);
    }
  };

  const etapas: Etapa[] = ['crianca', 'dados', 'assinatura'];
  const passoAtual = etapas.indexOf(etapa) + 1;

  return (
    <div className="checkout-overlay evento-page">
      <div className="evento-page-content">
        <div className="evento-logo-wrap">
          <img src="/logo.png" alt="Colônia de Férias" className="evento-logo" />
        </div>
        <div className="checkout-modal">
          <div className="checkout-header">
            <div>
              <h2>Termo de Autorização e Responsabilidade</h2>
              <p>Colônia de Férias - Clube UBA &gt; para a permanência na colônia</p>
            </div>
          </div>

          {!enviado && (
            <div className="evento-steps">
              {etapas.map((item, index) => (
                <span key={item} className={index + 1 <= passoAtual ? 'evento-step active' : 'evento-step'} />
              ))}
            </div>
          )}

          {carregandoDados && (
            <div className="checkout-result">
              <LoaderCircle className="spin" size={32} />
              <p>Carregando...</p>
            </div>
          )}

          {!carregandoDados && erroCarregamento && (
            <div className="checkout-form">
              <div className="checkout-error">{erroCarregamento}</div>
            </div>
          )}

          {!carregandoDados && !erroCarregamento && enviado && (
            <div className="checkout-result evento-success">
              <Check size={48} />
              <h3>Termo enviado com sucesso!</h3>
              <p>
                Obrigado, {nomeResponsavel.trim()}. O termo de {criancaSelecionada?.nomeCompleto} foi registrado.
              </p>
            </div>
          )}

          {!carregandoDados && !erroCarregamento && !enviado && etapa === 'crianca' && (
            <div className="checkout-form">
              <label>
                Nome da criança
                <input
                  value={criancaSelecionada ? criancaSelecionada.nomeCompleto : buscaCrianca}
                  onChange={(event) => {
                    setCriancaSelecionada(null);
                    setBuscaCrianca(event.target.value);
                  }}
                  placeholder="Digite o nome da criança"
                  autoFocus
                />
              </label>
              {!criancaSelecionada && sugestoes.length > 0 && (
                <ul className="evento-suggestions">
                  {sugestoes.map((crianca) => (
                    <li key={crianca.id}>
                      <button type="button" onClick={() => selecionarCrianca(crianca)}>
                        {crianca.nomeCompleto}
                        {crianca.turma ? <span> - Turma {crianca.turma}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              {!criancaSelecionada && buscaCrianca.trim().length >= 2 && sugestoes.length === 0 && (
                <p className="report-muted">Nenhuma criança encontrada com esse nome.</p>
              )}
              <button
                type="button"
                className="checkout-primary"
                disabled={!criancaSelecionada}
                onClick={() => setEtapa('dados')}
              >
                Continuar
              </button>
            </div>
          )}

          {!carregandoDados && !erroCarregamento && !enviado && etapa === 'dados' && (
            <div className="checkout-form">
              <button type="button" className="evento-voltar" onClick={() => setEtapa('crianca')}>
                <ChevronLeft size={16} /> Voltar
              </button>
              <p className="evento-crianca-selecionada">
                Criança: <strong>{criancaSelecionada?.nomeCompleto}</strong>
              </p>

              <div className="checkout-fields two-columns">
                <label>
                  Nome completo do responsável legal
                  <input value={nomeResponsavel} onChange={(event) => setNomeResponsavel(event.target.value)} />
                </label>
                <label>
                  CPF do responsável
                  <input
                    inputMode="numeric"
                    value={cpfResponsavel}
                    onChange={(event) => setCpfResponsavel(formatCpf(event.target.value))}
                    placeholder="000.000.000-00"
                  />
                </label>
                <label>
                  Telefone do responsável
                  <input value={telefoneResponsavel} onChange={(event) => setTelefoneResponsavel(formatPhone(event.target.value))} />
                </label>
                <label>
                  Telefone para emergência
                  <input value={telefoneEmergencia} onChange={(event) => setTelefoneEmergencia(formatPhone(event.target.value))} />
                </label>
              </div>

              <label>
                Alergias
                <textarea value={alergias} onChange={(event) => setAlergias(event.target.value)} rows={2} />
              </label>
              <label>
                Medicamentos de uso contínuo
                <textarea value={medicamentos} onChange={(event) => setMedicamentos(event.target.value)} rows={2} />
              </label>
              <label>
                Restrições alimentares
                <textarea value={restricoesAlimentares} onChange={(event) => setRestricoesAlimentares(event.target.value)} rows={2} />
              </label>
              <label>
                Outras informações relevantes
                <textarea value={outrasInformacoes} onChange={(event) => setOutrasInformacoes(event.target.value)} rows={2} />
              </label>

              <div className="checkout-fields two-columns">
                <label>
                  Convênio médico
                  <input value={convenioMedico} onChange={(event) => setConvenioMedico(event.target.value)} />
                </label>
                <label>
                  Número da carteirinha
                  <input value={numeroCarteirinha} onChange={(event) => setNumeroCarteirinha(event.target.value)} />
                </label>
              </div>

              <div className="evento-radio-group">
                <span>Autorização de uso de imagem</span>
                <label className="evento-radio-option">
                  <input type="radio" name="autorizaImagem" checked={autorizaImagem === 'sim'} onChange={() => setAutorizaImagem('sim')} />
                  Autorizo
                </label>
                <label className="evento-radio-option">
                  <input type="radio" name="autorizaImagem" checked={autorizaImagem === 'nao'} onChange={() => setAutorizaImagem('nao')} />
                  Não autorizo
                </label>
              </div>

              <div className="evento-pessoas-retirada">
                <span>Pessoas autorizadas a retirar a criança</span>
                {pessoasRetirada.map((pessoa, indice) => (
                  <div className="checkout-fields two-columns evento-pessoa-linha" key={indice}>
                    <label>
                      Nome
                      <input value={pessoa.nome} onChange={(event) => alterarPessoaRetirada(indice, { nome: event.target.value })} />
                    </label>
                    <label>
                      CPF
                      <div className="evento-pessoa-cpf-row">
                        <input
                          inputMode="numeric"
                          value={pessoa.cpf}
                          onChange={(event) => alterarPessoaRetirada(indice, { cpf: formatCpf(event.target.value) })}
                          placeholder="000.000.000-00"
                        />
                        {pessoasRetirada.length > 1 && (
                          <button type="button" onClick={() => removerPessoaRetirada(indice)} title="Remover">
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </label>
                  </div>
                ))}
                {pessoasRetirada.length < 3 && (
                  <button type="button" className="evento-voltar" onClick={adicionarPessoaRetirada}>
                    <Plus size={14} /> Adicionar pessoa
                  </button>
                )}
              </div>

              <button type="button" className="checkout-primary" disabled={!dadosValidos} onClick={() => setEtapa('assinatura')}>
                Continuar para assinatura
              </button>
            </div>
          )}

          {!carregandoDados && !erroCarregamento && !enviado && etapa === 'assinatura' && (
            <div className="checkout-form">
              <button type="button" className="evento-voltar" onClick={() => setEtapa('dados')}>
                <ChevronLeft size={16} /> Voltar
              </button>
              <p className="evento-crianca-selecionada">
                Assine com o dedo para confirmar o Termo de Autorização e Responsabilidade de{' '}
                <strong>{criancaSelecionada?.nomeCompleto}</strong>.
              </p>
              <SignaturePad
                drawn={assinaturaDesenhada}
                error=""
                initialDataUrl={assinaturaUrl}
                onChange={(drawn, dataUrl) => {
                  setAssinaturaDesenhada(drawn);
                  setAssinaturaUrl(dataUrl || '');
                }}
              />
              {erroEnvio && <div className="checkout-error">{erroEnvio}</div>}
              <button type="button" className="checkout-primary" disabled={!assinaturaUrl || enviando} onClick={enviarTermo}>
                {enviando && <LoaderCircle className="spin" size={18} />}
                Enviar termo assinado
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermoAutorizacaoPublico;
